import { ArrowRight } from 'lucide-react';
import LaunchCta from '@/components/landing/LaunchCta';
import { buttonClass } from '@/components/ui/Button';
import { CONTENT, PANEL, SECOND_LINE } from './roles';

/** A room to open the editor on (ADR 012 decision 8). Width → Lx, length → Ly. */
export interface EditorSeed {
  w: number;
  l: number;
  h: number;
  units: 'ft' | 'm';
  /** What the room is for, e.g. `studio` or `theater`. */
  use?: string;
}

/**
 * `/room/new`, carrying what the page knows as query params: the room (S12
 * makes `/room/new` read `w`/`l`/`h`/`units`/`use`) and the system — the gear
 * a gear-matching page is about, as the compatibility checker encodes it
 * (`encodeSelection` from @roomtreatment/av-compat: `avr=…&fl=…&layout=5.1`).
 *
 * The system travels as ONE parameter, `sys`, because its own keys would
 * collide with the room's (`h` is the room's height here and the height
 * speakers there). It arrives already encoded so this module, which renders
 * inside client widgets too, never pulls the compatibility engine into a
 * client bundle. Until `/room/new` reads a parameter the link still opens a
 * fresh room.
 *
 * A display (a TV, projector or screen model) has no role in that selection,
 * so it travels as its own `media=<id>` (av-compat's `DISPLAY_SEED_PARAM`)
 * and opens as the room's display.
 */
export function editorHref(seed?: EditorSeed, system?: string, display?: string): string {
  const params = new URLSearchParams();
  if (seed) {
    params.set('w', String(seed.w));
    params.set('l', String(seed.l));
    params.set('h', String(seed.h));
    params.set('units', seed.units);
    if (seed.use) params.set('use', seed.use);
  }
  if (system) params.set('sys', system);
  if (display) params.set('media', display);
  const query = params.toString();
  return query ? `/room/new?${query}` : '/room/new';
}

interface Props {
  /** Page-specific action, e.g. "Model this room in 3D". */
  label: string;
  seed?: EditorSeed;
  /** The gear to open the editor with: an `encodeSelection()` string. */
  system?: string;
  /** A display model to open the room with (`media=`). */
  display?: string;
  /** Analytics location for `landing_cta_clicked`. */
  location?: string;
  /** With `text`, renders as a panel: the sentence, an optional note, and the button. */
  text?: string;
  note?: string;
  testId?: string;
}

/**
 * The page's one primary action: open the 3D editor, on this page's room when
 * it has one. Bare, it is only the button; with `text` it is the closing panel.
 * No 'use client': it renders from server pages and from client widgets alike.
 */
export default function EditorCta({ label, seed, system, display, location = 'seo_page', text, note, testId }: Props) {
  const button = (
    <LaunchCta
      href={editorHref(seed, system, display)}
      location={location}
      data-cta="primary"
      className={buttonClass({ variant: 'primary', size: 'md', className: 'group whitespace-nowrap' })}
    >
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
    </LaunchCta>
  );
  const seedMarker = seed || system || display ? { 'data-seo': 'editor-seed' } : {};

  if (!text) {
    return (
      <div {...seedMarker} data-testid={testId}>
        {button}
      </div>
    );
  }
  return (
    <section
      {...seedMarker}
      data-testid={testId}
      className={`${PANEL} flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center`}
    >
      <div className="max-w-xl space-y-1">
        <p className={CONTENT}>{text}</p>
        {note && <p className={SECOND_LINE}>{note}</p>}
      </div>
      {button}
    </section>
  );
}
